import React from 'react';
import Hammer from 'hammerjs';
import { throttle } from 'lodash';
import { clampZoom, SignatureRequestContextShape, zoomContext, ZoomContextShape } from './context';
import styles from './document.scss';
import { useDragLayer, XYCoord } from 'react-dnd';

type Idle = { status: 'idle' }

type Blocked = { status: 'blocked' }

type Active = {
  readonly status: 'active',
  readonly startZoom: number,
  readonly zoomRefWidth: number,
  readonly originalCenter: XYCoord,
  readonly scrollLeft: number,
  readonly scrollTop: number,

  // These get updated on every event
  scale: number,
  // At the end of the gesture after manipulating the DOM, usePinchAndZoom will
  // notify the rest of the editor that the zoom has changed exactly once.
  newZoom: number,
}


function createDebugMarker(color: string, attachTo: HTMLElement) {
  const div = document.createElement('div');
  div.style.width = '5px';
  div.style.height = '5px';
  div.style.background = color;
  div.style.position = 'absolute';
  div.style.top = '0';
  div.style.left = '0';
  div.style.zIndex = '999';
  div.style.pointerEvents = 'none';
  attachTo.appendChild(div);
  return div;
}

type HammerEvent = {
  center: XYCoord,
  scale: number,
  srcEvent: PointerEvent,
  pointerType: 'mouse' | 'touch'
}

/**
 * This is all contained in a class because it needs to operate outside React's
 * normal render cycle.
 */
class HammerManager {
  readonly hammer: any;

  private state: Idle | Active | Blocked;

  constructor(
    private scrollContainerRef: SignatureRequestContextShape['pageContainerRef'],
    private zoomRef: React.MutableRefObject<HTMLElement | undefined>,
    // zoomContext is public so that the hook can keep it updated when it
    // changes.
    public zoomContext: ZoomContextShape,
  ) {
    if (!this.scrollContainerRef.current) {
      throw new Error('The scrollContianerRef needs to be ready before HammerJS is setup');
    }

    this.state = { status: 'idle' };

    // If the browser scrolls while panning around, it will cancel the gesture.
    document.body.classList.add(styles.blockScroll);

    this.hammer = new Hammer(this.scrollContainerRef.current, {
      // This can't use `pan-x pan-y`, because if Safari scrolls, it cancells
      // the pinch gesture.
      touchAction: 'none',
    });
    const hammer = this.hammer;
    hammer.get('pinch').set({ enable: true });
    hammer.get('pan').set({ direction: Hammer.DIRECTION_ALL });
    // Pan events have a scale of exactly 1, so they can be handled the same as a pinch
    // that doesn't change the scale.
    hammer.on('pinchstart panstart', this.start);
    hammer.on('pan pinchin pinchout', this.move);
    hammer.on('panend pinchend pinchcancel', this.end);

    // Only runs in Storybook
    // this.drawDebuggingMarkers();
  }

  destroy = () => {
    const hammer = this.hammer;
    hammer.on('pinchstart panstart', this.start);
    hammer.on('pan pinchin pinchout', this.move);
    hammer.on('panend pinchend pinchcancel', this.end);
    hammer.destroy();
  };

  setBlocked = (blocked: boolean) => {
    const stateSnapshot = this.state;
    if (blocked && stateSnapshot.status === 'active' && this.scrollContainerRef.current && this.zoomRef.current) {
      if (stateSnapshot.scale !== 1) {
        this.zoomRef.current.style.width = `${stateSnapshot.zoomRefWidth}px`;
      }

      this.scrollContainerRef.current.scrollLeft = stateSnapshot.scrollLeft;
      this.scrollContainerRef.current.scrollTop = stateSnapshot.scrollTop;
      this.state = { status: 'blocked' };
    }

    if (!blocked && stateSnapshot.status === 'blocked') {
      this.state = { status: 'idle' };
    }
  };

  // move needed to be throttled because handling the events too fast caused the
  // iPhone to flicker.
  move = throttle((event: HammerEvent) => {
    const stateSnapshot = this.state;

    if (stateSnapshot.status === 'active' && this.scrollContainerRef.current && this.zoomRef.current) {
      stateSnapshot.scale = Math.floor(event.scale * 1000) / 1000;
      if (event.srcEvent.shiftKey) {
        stateSnapshot.scale = 1.5;
      }

      // Update newZoom and scale, keeping it clamped within the min/max zoom
      stateSnapshot.newZoom = stateSnapshot.startZoom * stateSnapshot.scale;
      const clamped = clampZoom(stateSnapshot.newZoom);
      if (clamped !== stateSnapshot.newZoom) {
        stateSnapshot.newZoom = clamped;
        stateSnapshot.scale = clamped / stateSnapshot.startZoom;
      }

      // ////////////////////////////////////////////////////////////////
      // Please be careful with the math in this function. I got it to work,
      // but would have a hard time explaining how it works. Additional notes
      // and background info are available at:
      // https://www.dropbox.com/scl/fi/uql8hh15mn3ni0guostk2/DEV-18729-Zoom.paper?dl=0&rlkey=onarp0ieapxtbpq1ratdkfrzo
      // ////////////////////////////////////////////////////////////////

      const newWidth = stateSnapshot.zoomRefWidth * stateSnapshot.scale;
      this.zoomRef.current.style.width = `${newWidth}px`;

      const deltaX = stateSnapshot.originalCenter.x - event.center.x;
      const deltaY = stateSnapshot.originalCenter.y - event.center.y;

      const scrollLeft = stateSnapshot.scrollLeft
        * stateSnapshot.scale
        + (stateSnapshot.scale - 1)
        * event.center.x;

      const scrollRight = stateSnapshot.scrollTop
        * stateSnapshot.scale
        + (stateSnapshot.scale - 1)
        * event.center.y;

      this.scrollContainerRef.current.scrollLeft = scrollLeft + deltaX;
      this.scrollContainerRef.current.scrollTop = scrollRight + deltaY;
    }
  }, 10);

  start = (event: HammerEvent) => {
    const stateSnapshot = this.state;

    if (
      stateSnapshot.status === 'idle'
      && event.pointerType === 'touch'
      && this.scrollContainerRef.current
      && this.zoomRef.current
    ) {
      const scrollLeft = this.scrollContainerRef.current.scrollLeft;
      const scrollTop = this.scrollContainerRef.current.scrollTop;
      const zoomRefWidth = this.zoomRef.current.clientWidth;

      this.state = {
        status: 'active',
        startZoom: this.zoomContext.zoom,
        scrollLeft,
        scrollTop,
        originalCenter: event.center,
        zoomRefWidth,
        scale: event.scale,
        newZoom: this.zoomContext.zoom * event.scale,
      };
    }
  };

  end = () => {
    const stateSnapshot = this.state;
    if (stateSnapshot.status === 'active') {
      this.state = { status: 'idle' };
      this.zoomContext.setZoom(stateSnapshot.newZoom, null);
    }
  };


  drawDebuggingMarkers = () => {
    if (!process.env.IS_STORYBOOK) {
      return;
    }
    const containerMarker = createDebugMarker('red', this.scrollContainerRef.current!);

    setInterval(() => {
      const stateSnapshot = this.state;

      if (stateSnapshot.status === 'active') {
        const viewportCoords = {
          x: 0,
          y: 0,
        };

        containerMarker.style.top = `${viewportCoords.y}px`;
        containerMarker.style.left = `${viewportCoords.x}px`;
      }
    }, 100);
  };
}

/**
 * NOTE: This updates the DOM directly
 *
 * Normally in React, events should change data, then React will re-render.  If
 * I try to change the zoom continuously while pinching/zooming on an iPhone,
 * it doesn't perform well enough.  This hook uses HammerJS to detect
 * pinch/zoom and updates the DOM directly. At ther end of the gesture, the
 * zoom will be updated once, causing React to re-render.
 *
 * Additional notes on usePinchAndZoom are available at:
 * https://www.dropbox.com/scl/fi/uql8hh15mn3ni0guostk2/DEV-18729-Zoom.paper?dl=0&rlkey=onarp0ieapxtbpq1ratdkfrzo
 */
export function usePinchAndZoom(
  // scrollContainerRef is the div that can be scrolled with scrollTop/scrolLeft
  scrollContainerRef: SignatureRequestContextShape['pageContainerRef'],
  // zoomRef is the div that is made larger or smaller to scale pages
  zoomRef: React.MutableRefObject<HTMLElement | undefined>,
  // isOverlay is a boolean to know if it's rendering from overlay
  isOverlay: boolean,
) {
  const managerRef = React.useRef<HammerManager>();

  const currentZoomContext = React.useContext(zoomContext);
  const isDragging = useDragLayer((monitor) => monitor.isDragging());
  if (managerRef.current) {
    managerRef.current.zoomContext = currentZoomContext;
    managerRef.current.setBlocked(isDragging);
  }

  React.useEffect(() => {
    // Only create a manager once
    // If we create a manager when it's overlay it breaks
    // When rendering the overlay
    if (!managerRef.current && !isOverlay) {
      managerRef.current = new HammerManager(
        scrollContainerRef,
        zoomRef,
        currentZoomContext,
      );
    }
  }, [isOverlay, currentZoomContext, scrollContainerRef, zoomRef]);

  React.useEffect(() => {
    const manager = managerRef.current;

    return () => {
      if (manager) {
        manager.destroy();
      }
    };
  }, []);

}
